import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, CheckCircle2, Clock, Navigation, Briefcase, Users, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import ServiceFAQ from "@/components/ServiceFAQ";
import { generateLocalBusinessSchema } from "@/lib/schemaGenerator";
import hourlyServiceImg from "@/assets/hourly-service.webp";
const HourlyService = () => {
  const faqs = [
    {
      question: "What is the minimum hourly booking?",
      answer: "Our hourly service typically has a 2-hour minimum. This ensures you have sufficient time for your itinerary while maintaining the flexibility our hourly service is known for."
    },
    {
      question: "Can I change my itinerary during the hourly service?",
      answer: "Absolutely! That's the beauty of our as-directed hourly service. You can modify your stops and destinations as needed throughout your reserved time. Just let your chauffeur know."
    },
    {
      question: "What types of activities work well with hourly service?",
      answer: "Hourly service is perfect for multiple business meetings, shopping excursions, city tours, wine tasting tours, event transportation with multiple venues, and any itinerary requiring flexibility with multiple stops."
    },
    {
      question: "How do I book hourly car service in San Antonio?",
      answer: "Call us at (210) 296-7409 or book online. Let us know your pickup time, estimated duration, and general itinerary. We'll confirm availability and provide transparent hourly rates."
    },
    {
      question: "Can I extend my hourly booking if needed?",
      answer: "Yes! If you need more time during your service, just let your chauffeur know. We'll extend your booking based on availability and charge only for the additional time used."
    }
  ];
  
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/services/hourly-service#service",
    "name": "Hourly Car Service San Antonio - As-Directed Chauffeur",
    "serviceType": "Hourly Chauffeur Service",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "url": "https://ricksblackcarservicesantonio.com"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio",
      "addressRegion": "Texas"
    },
    "description": "Professional hourly chauffeur service in San Antonio with private driver for multiple stops. Flexible luxury car service for business meetings, shopping, errands, city tours, and special events. 2-hour minimum, half-day and full-day packages available.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "category": "Hourly Chauffeur Service",
    "serviceOutput": "Flexible transportation with dedicated chauffeur for multiple destinations",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Hourly Service Packages",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "2-Hour Minimum Service",
          "description": "Short errands, shopping trips, or quick business meetings with flexible routing",
          "price": "270",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "2-Hour Minimum Service"
          }
        },
        {
          "@type": "Offer",
          "name": "Half-Day Service (4-6 hours)",
          "description": "Extended business meetings, property tours, or leisure activities around San Antonio",
          "price": "540",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "Half-Day Service (4 hours)"
          }
        },
        {
          "@type": "Offer",
          "name": "Full-Day Service (8+ hours)",
          "description": "All-day availability for business roadshows, special events, or comprehensive city tours",
          "price": "1080",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "Full-Day Service (8 hours)"
          }
        }
      ]
    },
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/services/hourly-service",
      "priceCurrency": "USD",
      "lowPrice": "135",
      "highPrice": "1080",
      "offerCount": "3",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock",
      "validFrom": "2024-01-01",
      "seller": {
        "@type": "LocalBusiness",
        "name": "Rick's Black Car Service",
        "telephone": "+1-210-296-7409"
      }
    },
    "priceRange": "$135/hour (2-hour minimum)",
    "additionalType": "https://schema.org/TaxiService",
    "audience": {
      "@type": "Audience",
      "audienceType": "Business Professionals, Tourists, Shoppers, Event Attendees"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "servicePhone": {
        "@type": "ContactPoint",
        "telephone": "+1-210-296-7409",
        "contactType": "Reservations"
      },
      "serviceUrl": "https://ricksblackcarservicesantonio.com/services/hourly-service"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return <>
      <SEO title="Luxury Hourly Chauffeur Service San Antonio | Rick's Black Car Service" description="Book a private 2025 Audi Q7 by the hour in San Antonio. Rates start at $135/hr (2-hour minimum). Perfect for executive travel, special events, or nights out. 24/7 availability." canonicalPath="/services/hourly-service" schema={[schemaMarkup, faqSchema]} />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb 
            items={[
              { name: "Services", url: "/services" },
              { name: "Hourly Service", url: "/services/hourly-service" }
            ]}
          />

          {/* Hero Section */}
          <section className="relative h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <img src={hourlyServiceImg} alt="Hourly car service San Antonio - private driver" className="w-full h-full object-cover brightness-90" />
              <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/40 to-background/60" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground mb-6">
                Hourly Chauffeur Service in San Antonio
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Private Driver San Antonio | Flexible Luxury Transportation | Available 24/7
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now: 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  
                </Button>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                {/* Unique Pricing Highlight */}
                <Card className="card-luxury mb-12 border-2 border-primary/30 bg-primary/5">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <h2 className="text-3xl font-serif font-bold text-foreground mb-4">
                        Premium Hourly Charter Service
                      </h2>
                      <div className="text-5xl font-bold text-primary mb-2">$135<span className="text-xl text-muted-foreground">/hour</span></div>
                      <p className="text-muted-foreground text-lg mb-4">2-hour minimum booking required</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-left">
                        <div className="bg-background/50 rounded-lg p-4">
                          <div className="font-semibold text-foreground">2025 Audi Q7 Black Optic</div>
                          <div className="text-sm text-muted-foreground">Premium SUV with black exterior</div>
                        </div>
                        <div className="bg-background/50 rounded-lg p-4">
                          <div className="font-semibold text-foreground">Up to 6 Passengers</div>
                          <div className="text-sm text-muted-foreground">Spacious leather seating</div>
                        </div>
                        <div className="bg-background/50 rounded-lg p-4">
                          <div className="font-semibold text-foreground">Available 24/7</div>
                          <div className="text-sm text-muted-foreground">Late-Night/Early Morning $35 surcharge (10PM-5AM)</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    Rick's Black Car Service offers <strong className="text-foreground">premium hourly charter service</strong> in San Antonio starting at <strong className="text-foreground">$135/hour</strong> with a 2-hour minimum. Experience the comfort of our <strong className="text-foreground">2025 Audi Q7 Black Optic SUV</strong> with your dedicated professional chauffeur for multiple stops, business meetings, shopping excursions, or special occasions.
                  </p>
                  
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4 mt-8">
                    Perfect For
                  </h2>
                  <ul className="space-y-3 text-muted-foreground text-lg mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Executive Travel</strong> - Multiple meetings, client visits, property tours with flexible scheduling at $135/hour</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Personal Errands</strong> - Shopping at La Cantera, The Rim, medical appointments, sightseeing in our luxury Audi Q7</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Special Occasions</strong> - Anniversary dinners, birthday celebrations, nights out in San Antonio</span>
                    </li>
                  </ul>
                  
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4 mt-8">
                    Benefits
                  </h2>
                  <ul className="space-y-3 text-muted-foreground text-lg mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Complete flexibility to change plans</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Time efficiency - no booking multiple rides</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Cost-effective for multiple stops</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Professional chauffeur at your service</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>No parking hassles</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Privacy and discretion</span>
                    </li>
                  </ul>
                </div>

                {/* CTA Card */}
                <Card className="card-luxury my-12 border-2 border-primary/20">
                  <CardContent className="p-10 text-center">
                    <h2 className="text-3xl font-serif font-bold text-foreground mb-4">
                      Book Your Hourly Car Service
                    </h2>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        <a href="tel:+12102967409">
                          📞 Call Now: 210-296-7409
                        </a>
                      </Button>
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                          Reserve Black Car Service
                        </a>
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-lg">
                      Available 24/7 | Professional Chauffeurs | Luxury Fleet
                    </p>
                  </CardContent>
                </Card>

                {/* How It Works */}
                <div className="my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                    How It Works
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <div className="text-primary text-4xl font-bold mb-2">1</div>
                        <p className="text-muted-foreground">Book your time (minimum 2-3 hours or full day)</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <div className="text-primary text-4xl font-bold mb-2">2</div>
                        <p className="text-muted-foreground">Your chauffeur arrives at scheduled time</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <div className="text-primary text-4xl font-bold mb-2">3</div>
                        <p className="text-muted-foreground">Travel at your pace with multiple stops</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <div className="text-primary text-4xl font-bold mb-2">4</div>
                        <p className="text-muted-foreground">Extend if needed based on availability</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Features */}
                <div className="my-12">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-6 text-center">Features</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">Professional chauffeur</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">Luxury 2025 Audi Q7</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">Up to 6 passengers</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">Complimentary water</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">Climate control</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">Flexible routing</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">Free Complimentary WiFi</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Service Features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12">
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Clock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Flexible Scheduling</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            Book your private driver San Antonio for 2 hours, half-day, or full-day service. Extend your booking anytime during service if your plans change. Our hourly car service San Antonio adapts to your schedule, not the other way around.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Navigation className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Multiple Stops</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            Perfect for business errands, shopping trips, or sightseeing tours with multiple destinations. Your chauffeur waits while you attend to each stop, then proceeds to your next location. Unlimited stops included within your booked time.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Briefcase className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Business Efficiency</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            Executive car service San Antonio TX for productive business days. Take calls, review documents, or relax between meetings in our luxury vehicles. Wi-Fi and charging ports keep you connected. Your private driver handles all logistics.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Users className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Group Transportation</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            Our luxury SUVs accommodate small groups for corporate team outings, family tours, or group celebrations. Hourly chauffeur service San Antonio for winery tours, Hill Country excursions, or exploring San Antonio attractions together.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Additional Content */}
                <div className="prose prose-lg max-w-none my-12">
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    Call (210) 296-7409 for hourly rate quotes.
                  </p>
                </div>

                {/* Pricing Structure */}
                <Card className="card-luxury my-12">
                  <CardContent className="p-10">
                    <h3 className="text-2xl font-serif font-bold text-foreground mb-6 text-center">
                      Hourly Service Options
                    </h3>
                    <div className="space-y-4 text-muted-foreground">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <strong className="text-foreground">2-Hour Minimum:</strong> Perfect for quick errands, shopping trips, or short business appointments in San Antonio
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <strong className="text-foreground">Half-Day (4-6 Hours):</strong> Ideal for comprehensive business days, extended shopping, or city tours
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <strong className="text-foreground">Full-Day (8-10 Hours):</strong> Complete coverage for major events, executive days, or extensive travel throughout San Antonio area
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <strong className="text-foreground">Extended Service:</strong> Multi-day charters available for conferences, celebrations, or visiting executives
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* FAQ Section */}
                <ServiceFAQ faqs={faqs} />

                {/* Final CTA */}
                <div className="text-center my-12">
                  <h2 className="text-3xl font-serif font-bold text-foreground mb-6">
                    Experience True Transportation Freedom
                  </h2>
                  <p className="text-muted-foreground text-lg mb-8">
                    Book your hourly car service San Antonio today and discover the convenience of having a professional private driver at your disposal. Call now for instant quotes and availability.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button className="btn-luxury text-xl px-10 py-7" asChild>
                      <a href="tel:+12102967409">
                        <Phone className="w-6 h-6 mr-2" />
                        Call: 210-296-7409
                      </a>
                    </Button>
                    <Button className="btn-luxury text-xl px-10 py-7" asChild>
                      <Link to="/book">
                        Book Your Ride Now <ArrowRight className="w-6 h-6 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>;
};
export default HourlyService;